import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

const LOGIN_URL = `${environment.apiUrl}/api/auth/login`;
const REFRESH_URL = `${environment.apiUrl}/api/auth/refresh`;
const LOGOUT_URL = `${environment.apiUrl}/api/auth/logout`;

function attachToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
	if (!token) return req;
	return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
	// These authenticate on the refresh cookie, not a bearer token, and handle
	// their own failures — recursing into refresh from a failed refresh loops.
	if (req.url === LOGIN_URL || req.url === REFRESH_URL || req.url === LOGOUT_URL) {
		return next(req);
	}

	const auth = inject(AuthService);
	const authed = attachToken(req, auth.accessToken());

	return next(authed).pipe(
		catchError((err: HttpErrorResponse): Observable<never> => {
			if (err.status !== 401) {
				return throwError(() => err);
			}

			return auth.refresh().pipe(
				switchMap((newAccess) =>
					next(attachToken(req, newAccess)).pipe(
						catchError((retryErr: HttpErrorResponse) => {
							// A token minted seconds ago being rejected means the session
							// really is over. This used to be swallowed, leaving the user
							// on a page where every request silently failed.
							if (retryErr.status === 401) auth.endSession();
							return throwError(() => retryErr);
						}),
					),
				),
				// refresh() already decided the session's fate; surface the
				// original 401 to the caller either way.
				catchError(() => throwError(() => err)),
			) as Observable<never>;
		}),
	);
};
